import type { ReactNode } from "react";
import { Code } from "@connectrpc/connect";
import { useQuery } from "@connectrpc/connect-query";
import { Link, createFileRoute } from "@tanstack/react-router";

import { formatDate, formatNepalTime } from "@yatayat/core/date";
import { formatPaisa } from "@yatayat/core/money";
import { BookingService, type Ticket } from "@yatayat/gen/yatayat/booking/v1/booking_pb";

import { PhoneSignIn } from "~/components/PhoneSignIn";
import { Loading, QueryError } from "~/components/QueryState";
import { TicketStatusBadge } from "~/components/TicketStatusBadge";
import { useI18n } from "~/lib/i18n";
import { localDigits, placeName } from "~/lib/labels";
import { errorCode, retryTransient } from "~/lib/rpc/errors";
import { ui } from "~/lib/ui";

export const Route = createFileRoute("/tickets/")({
  component: MyTickets,
});

function MyTickets() {
  const { t } = useI18n();
  const tickets = useQuery(BookingService.method.listMyTickets, {}, { retry: retryTransient });

  let body: ReactNode;
  if (tickets.data) {
    body =
      tickets.data.tickets.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center dark:border-slate-700">
          <p className="text-slate-600 dark:text-slate-400">{t("noTickets")}</p>
          <Link
            to="/book"
            className="mt-3 inline-block rounded-md bg-ink px-4 py-2 text-sm font-medium text-white dark:bg-ink-dark dark:text-slate-950"
          >
            {t("bookSeat")}
          </Link>
        </div>
      ) : (
        <ul className="space-y-3">
          {tickets.data.tickets.map((ticket) => (
            <TicketRow key={ticket.id} ticket={ticket} />
          ))}
        </ul>
      );
  } else if (tickets.isError) {
    // Signing in resets this query, so the list loads by itself afterwards.
    body =
      errorCode(tickets.error) === Code.Unauthenticated ? (
        <div className="max-w-md rounded-xl border border-slate-200 p-4 dark:border-slate-800">
          <PhoneSignIn intro={t("signInForTickets")} />
        </div>
      ) : (
        <QueryError onRetry={() => void tickets.refetch()} busy={tickets.isFetching} />
      );
  } else {
    body = <Loading />;
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <h1 className={ui.h1}>{t("navTickets")}</h1>
      {body}
    </div>
  );
}

function TicketRow({ ticket }: { ticket: Ticket }) {
  const { t, lang } = useI18n();
  const departs = Number(ticket.trip?.departsAtMs ?? 0n);

  return (
    <li>
      <Link
        to="/tickets/$ticketId"
        params={{ ticketId: ticket.id }}
        className="block rounded-xl border border-slate-200 p-4 hover:border-ink/40 hover:bg-slate-50 dark:border-slate-800 dark:hover:border-ink-dark/40 dark:hover:bg-slate-900"
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="font-mono text-lg font-semibold tracking-wide">{ticket.code}</span>
          <TicketStatusBadge status={ticket.status} />
        </div>
        <p className="mt-1 font-medium">
          {placeName(ticket.trip?.from, lang)} → {placeName(ticket.trip?.to, lang)}
        </p>
        {ticket.trip?.routeName?.[lang] && (
          <p className="text-sm text-slate-600 dark:text-slate-400">{ticket.trip.routeName[lang]}</p>
        )}
        <div className="mt-1 flex flex-wrap items-baseline justify-between gap-2 text-sm">
          <span className="text-slate-600 dark:text-slate-400">
            {departs > 0 && `${formatDate(departs, lang)} · ${formatNepalTime(departs, lang)} · `}
            {t("seatNo", { no: localDigits(ticket.seatNo, lang) })}
          </span>
          <span className="font-semibold">{formatPaisa(ticket.chargedFare?.paisa ?? 0n, lang)}</span>
        </div>
      </Link>
    </li>
  );
}
